import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useGlobalWebSocket } from '../hooks/WebSocketProvider.tsx';
import { useTenantContext } from '../hooks/useTenantContext.ts';
import { conversationsApi } from '../lib/conversations-api.js';
import { MenuMovil } from './MenuMovil.tsx';
import { UserMenu } from './UserMenu.tsx';
import { ThemeToggle } from './ui/ThemeToggle.tsx';

interface NavItem {
  path: string;
  label: string;
  /** Coincidencia exacta: sin esto `/tenants` quedaría activo dentro de `/tenants/x`. */
  end?: boolean;
}

/**
 * Navegación principal.
 *
 * Son cuatro y no las siete del sidebar viejo: en horizontal el espacio es el
 * recurso escaso, y "Nuevo tenant" es una acción de la página de Tenants, no un
 * destino. "Usuarios" y "Operadores" viven detrás de sus secciones.
 */
const NAV_ITEMS: NavItem[] = [
  { path: '/tenants', label: 'Tenants' },
  { path: '/conversations', label: 'Conversaciones', end: true },
  { path: '/contacts', label: 'Contactos' },
  { path: '/tickets', label: 'Tickets' },
  { path: '/metrics', label: 'Métricas' },
  // Va al final: no es un destino diario, es lo que se abre cuando cambiaste
  // el conocimiento y querés ver cómo quedó (C7).
  { path: '/probar', label: 'Probá tu bot' },
];

/**
 * Barra superior fija: marca, navegación principal y cuenta.
 *
 * Reemplaza el sidebar de 240px. El ancho que libera es el punto del cambio —
 * las tablas del CRM se leen mucho mejor con esos 240px de vuelta.
 */
export function TopBar() {
  const { pendingCounts, lastReconnectAt } = useGlobalWebSocket();
  const { activeTenantId } = useTenantContext();
  const [reconciliado, setReconciliado] = useState<number | null>(null);

  // El contador del WebSocket sólo se mueve con los eventos que llegan mientras
  // la pestaña está abierta: al cargar la página arrancaba en cero aunque
  // hubiera gente esperando. Se reconcilia contra la API al montar y cada vez
  // que el socket se reconecta.
  // biome-ignore lint/correctness/useExhaustiveDependencies: lastReconnectAt es un trigger deliberado de re-sincronización
  useEffect(() => {
    if (!activeTenantId) return;
    let cancelado = false;
    conversationsApi
      .list({ tenantId: activeTenantId, state: 'pending_human', limit: 100 })
      .then((res) => {
        if (!cancelado) setReconciliado(res.items.length);
      })
      .catch(() => {
        // El badge no vale una pantalla de error: si falla, queda el del socket.
      });
    return () => {
      cancelado = true;
    };
  }, [activeTenantId, lastReconnectAt]);

  const enVivo = activeTenantId ? pendingCounts[activeTenantId] : undefined;
  const pendientes = enVivo ?? reconciliado ?? 0;

  return (
    <header
      // `<header>` ya trae el rol `banner` implícito: declararlo es redundante.
      data-testid="topbar"
      className="flex h-14 shrink-0 items-center border-b border-edge-subtle bg-surface-base px-4 md:px-6"
    >
      <NavLink
        to="/tenants"
        aria-label="Tibo CRM — inicio"
        className="mr-4 flex items-center gap-2 font-semibold text-content-brand md:mr-8"
      >
        {/* El logo real, no un marcador. `alt=""` porque el texto de al lado
            ya dice el nombre: un lector de pantalla que lea las dos cosas
            repite "Tibo Tibo CRM". */}
        <img src="/tibo-icon.svg" alt="" aria-hidden="true" className="h-7 w-7 shrink-0" />
        <span>Tibo CRM</span>
      </NavLink>

      {/* En ancho de teléfono la barra no entraba y dos secciones quedaban
          inalcanzables (GKTIBO-108). El menú las devuelve; en escritorio no
          aparece y la barra queda exactamente igual. */}
      <MenuMovil items={NAV_ITEMS} pendientes={pendientes} />

      <nav aria-label="Navegación principal" className="hidden flex-1 items-center gap-1 md:flex">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            className={({ isActive }) =>
              `relative rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-surface-brand-soft text-content-brand'
                  : 'text-content-secondary hover:bg-surface-sunken'
              }`
            }
          >
            {item.label}
            {item.path === '/conversations' && pendientes > 0 ? (
              <span
                data-testid="pending-badge"
                aria-label={`${pendientes} conversaciones esperando atención`}
                className="ml-2 rounded-full bg-danger px-1.5 py-0.5 text-xs font-semibold text-white"
              >
                {pendientes}
              </span>
            ) : null}
          </NavLink>
        ))}
      </nav>

      <div className="flex items-center gap-2">
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}
